import CustomSearchBar from "components/CustomSearchBar";
import CustomTable from "components/CustomTable";
import { useEffect, useState } from "react";
import { Row, Col, Card, CardHeader, CardBody, Button } from "reactstrap";
import { Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import { useGetCommissionBatchQuery } from "features/SaathiManagement/Commision/CommissionApiSlice";
import BankingTabSection from "./BankingTabSection";
import { useSelector } from "react-redux";
import { selectCurrentRoles } from "features/auth/authSlice";
import useDebouncedValue from "hooks/useDebouncedValue";
import Select from "react-select";
import { Link } from "react-router-dom";
import { CheckCircleOutlined } from "@mui/icons-material";
import { FileTextOutlined } from "@ant-design/icons";
const BankingInvoice = () => {
  const RolesFromToken = useSelector(selectCurrentRoles);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageCount: 0 });
  const [searchKey, setSearchKey] = useState("");
  const [bankFilter, setBankFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const debouncedSearch = useDebouncedValue(searchKey);
  const [statusName, setStatusName] = useState("");
  const { data, isLoading, isFetching, refetch } = useGetCommissionBatchQuery({
    page: pagination.page,
    per_page: pagination.per_page,
    searchKey: debouncedSearch,
    bank_name: bankFilter,
    year: yearFilter,
    month: monthFilter,
    status: statusName,
  });

  const bankOptions = [
    { label: "Bandhan Bank", value: "Bandhan Bank" },
    { label: "Bank Of Baroda", value: "Bank Of Baroda" },
    { label: "Punjab and Sind Bank", value: "Punjab and Sind Bank" },
    { label: "State Bank of India", value: "State Bank of India" },
  ];

  const yearOptions = [
    { label: "2023", value: "2023" },
    { label: "2024", value: "2024" },
    { label: "2025", value: "2025" },
  ];

  const monthOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ].map((m) => ({ label: m, value: m }));

  useEffect(() => {
    const dataFromApi = data?.data || [];
    const paginationFromApi = data?.pagination
      ? {
          page: data.pagination.page,
          pageCount: data.pagination.total_pages,
          per_page: data.pagination.per_page,
          total: data.pagination.total,
        }
      : pagination;
    setUsers(dataFromApi);
    setPagination(paginationFromApi);
  }, [data]);

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 70,
      sortable: false,
      renderCell: (cellValues) => (
        <Link to={`/banking/invoice/${cellValues.row.bill_no}`}>
          <div>
            <FontAwesomeIcon
              icon={faEye}
              style={{
                fontSize: "14px",
                cursor: "pointer",
              }}
              className="mx-2 text-warning"
            />
          </div>
        </Link>
      ),
    },

    {
      field: "bill_no",
      headerName: "Bill No",
      width: 220,
      renderCell: (cellValues) => (
        <strong style={{ fontSize: "13px", color: "#555252" }}>
          {cellValues?.row.bill_no}
        </strong>
      ),
    },

    {
      field: "bank_name",
      headerName: "Bank",
      width: 180,
      renderCell: (cellValues) => (
        <span style={{ fontWeight: 500 }}>{cellValues?.row.bank_name}</span>
      ),
    },

    {
      field: "month",
      headerName: "Month / Year",
      width: 140,
      renderCell: (cellValues) => (
        <span>
          {cellValues?.row.month} {cellValues?.row.year}
        </span>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (cellValues) => {
        const status = cellValues.row.status;
        const statusConfig = {
          "Invoice Raised": { color: "volcano", icon: <CheckCircleOutlined /> },
          "Payment Completed": { color: "green", icon: <FileTextOutlined /> },
        };
        const { color, icon } = statusConfig[status] || {
          color: "gold",
          icon: <FileTextOutlined />,
        };

        return (
          <Tag
            bordered={false}
            color={color}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "3px 9px",
              fontSize: "13px",
              fontWeight: "500",
              borderRadius: "6px",
            }}
          >
            <span>{status}</span>
          </Tag>
        );
      },
    },

    {
      field: "loan_amount_sanctioned",
      headerName: "Sanctioned Amt (₹)",
      width: 170,
      renderCell: (cellValues) => (
        <span style={{ color: "#555252", fontWeight: "800" }}>
          ₹ {cellValues?.row.loan_amount_sanctioned?.toLocaleString("en-IN")}
        </span>
      ),
    },

    {
      field: "commission_percent",
      headerName: "Commission %",
      width: 130,
      renderCell: (cellValues) => `${cellValues?.row.commission_percent}%`,
    },

    {
      field: "total_commission_amount",
      headerName: "Commission (₹)",
      width: 170,
      renderCell: (cellValues) => (
        <span style={{ color: "#16a34a", fontWeight: 600 }}>
          ₹ {cellValues?.row.total_commission_amount?.toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      field: "total_commission_amount_with_gst",
      headerName: "Commission inc. (GST) (₹)",
      width: 180,
      renderCell: (cellValues) => (
        <span style={{ color: "#16a34a", fontWeight: 600 }}>
          ₹ {cellValues?.row.total_commission_amount?.toLocaleString("en-IN")}
        </span>
      ),
    },

    {
      field: "gst_percent",
      headerName: "GST %",
      width: 100,
      renderCell: (cellValues) => `${cellValues?.row.gst_percent}%`,
    },

    {
      field: "loan_amount_sanctioned_with_gst",
      headerName: "GST Amount (₹)",
      width: 170,
      renderCell: (cellValues) =>
        `₹ ${cellValues?.row.loan_amount_sanctioned_with_gst?.toLocaleString(
          "en-IN"
        )}`,
    },

    {
      field: "loan_amount_sanctioned_with_cgst",
      headerName: "CGST (₹)",
      width: 150,
      renderCell: (cellValues) =>
        `₹ ${cellValues?.row.loan_amount_sanctioned_with_cgst?.toLocaleString(
          "en-IN"
        )}`,
    },

    {
      field: "loan_amount_sanctioned_with_sgst",
      headerName: "SGST (₹)",
      width: 150,
      renderCell: (cellValues) =>
        `₹ ${cellValues?.row.loan_amount_sanctioned_with_sgst?.toLocaleString(
          "en-IN"
        )}`,
    },
  ];

  const tableData = {
    rows: users,
    columns: columns,
    paginationState: [pagination, setPagination],
    isLoading: isLoading || isFetching,
    checkboxShowFalse: true,
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      height: "38px",
      borderRadius: "8px",
      fontSize: "13px",
      padding: "0 2px",

      transition: "all 0.2s ease",

      alignItems: "center",
    }),

    valueContainer: (provided) => ({
      ...provided,
      padding: "0 8px",
      display: "flex",
      alignItems: "center",
      height: "38px",
    }),

    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      height: "auto",
    }),

    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
    }),

    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: "6px",
      transition: "color 0.2s ease",
    }),

    clearIndicator: (provided) => ({
      ...provided,
      padding: "6px",
      color: "#9ca3af",
    }),

    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      marginTop: 4,
      fontSize: "13px",
      zIndex: 5,
    }),

    option: (provided, state) => ({
      ...provided,
      fontSize: "13px",
      padding: "8px 10px",
      cursor: "pointer",
    }),

    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
      marginLeft: 2,
    }),

    singleValue: (provided) => ({
      ...provided,
      color: "#111827",
      marginLeft: 2,
      lineHeight: "1",
    }),
  };

  const downloadExcel = async ({ pagination }) => {
    const rows = users.map((u) => ({
      "Bill No": u.bill_no,
      "Bank Name": u.bank_name,
      Month: u.month,
      Year: u.year,
      "Sanctioned Amount": u.loan_amount_sanctioned,
      "Commission (%) ": u.total_commission_amount,
      "Commission (₹) ": u.total_commission_amount,
      "Commission inc. (GST)": u.total_commission_amount_with_gst,
      "GST (%) ": u.gst_percent,
      "GST Amount (₹) ": u.loan_amount_sanctioned_with_gst,
      CGST: u.loan_amount_sanctioned_with_cgst,
      SGST: u.loan_amount_sanctioned_with_sgst,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applications");
    XLSX.writeFile(workbook, "Loan Applications.csv");
  };

  const handleChangeSearch = (message) => {
    setSearchKey(message);
  };
  return (
    <div>
      {(RolesFromToken?.groups?.includes("Banking Admin") ||
!( RolesFromToken?.groups?.includes("State Head") ||
        RolesFromToken?.groups?.includes("District Head") ||
        RolesFromToken?.groups?.includes("Cluster Head") 
      )) && <BankingTabSection activeTab="Invoicing" />}
      <div className="mt-3">
        <Card>
          <CardBody className="p-1 p-lg-3">
            <CardHeader className="">
              <Row className=" d-flex w-100 align-items-center mt-1">
                <Col md="4">
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <CustomSearchBar sendSearchFunction={handleChangeSearch} />
                    <span className="fw-semibold text-success mx-4">
                      Total : {data?.pagination?.total || 0}
                    </span>
                  </div>
                </Col>

                <Col
                  md="6"
                  lg="4"
                  className="d-flex justify-content-end gap-2 align-items-center offset-md-4"
                >
                  <Button
                    id="AddLeads"
                    type=""
                    className="buttonGreenDefaultColor"
                    size="sm"
                    onClick={downloadExcel}
                  >
                    Download CSV{" "}
                    <i className="mdi mdi-download custom-btn-fs fs-6 mx-1"></i>
                  </Button>
                  <Button
                    className="bg-transparent text-black"
                    style={{
                      border: "1px solid #829999",
                      color: "#343737",
                      marginTop: "0",
                    }}
                    size="sm"
                    onClick={() => refetch()}
                  >
                    <FontAwesomeIcon icon={faRotateLeft} />
                  </Button>
                </Col>
              </Row>
              <Row className="mt-1 g-3 align-items-center">
                <Col md="2">
                  <Select
                    options={bankOptions}
                    placeholder="Select Bank"
                    value={
                      bankOptions.find((opt) => opt.value === bankFilter) ||
                      null
                    }
                    onChange={(e) => {
                      setBankFilter(e ? e.value : "");
                      setPagination({ page: 1, pageCount: 0 });
                    }}
                    styles={customStyles}
                    isClearable
                  />
                </Col>

                <Col md="2">
                  <Select
                    options={yearOptions}
                    placeholder="Select Year"
                    value={
                      yearOptions.find((opt) => opt.value === yearFilter) ||
                      null
                    }
                    onChange={(e) => {
                      setYearFilter(e ? e.value : "");
                      setPagination({ page: 1, pageCount: 0 });
                    }}
                    styles={customStyles}
                    isClearable
                  />
                </Col>

                <Col md="2">
                  <Select
                    options={monthOptions}
                    placeholder="Select Month"
                    value={
                      monthOptions.find((opt) => opt.value === monthFilter) ||
                      null
                    }
                    onChange={(e) => {
                      setMonthFilter(e ? e.value : "");
                      setPagination({ page: 1, pageCount: 0 });
                    }}
                    styles={customStyles}
                    isClearable
                  />
                </Col>
                <Col sm="2" className="mb-2 position-relative mt-4">
                  <Select
                    menuPlacement="auto"
                    options={[
                      { value: "Pending", label: "Pending" },
                      {
                        value: "Invoice Raised",
                        label: "Invoice Raised",
                      },
                      {
                        value: "Payment Completed",
                        label: "Payment Completed",
                      },
                    ]}
                    placeholder="Select Status"
                    value={
                      statusName
                        ? { value: statusName, label: statusName }
                        : null
                    }
                    onChange={(e) => setStatusName(e.value)}
                    styles={customStyles}
                  />
                  {statusName && (
                    <span
                      className="clear-icon"
                      onClick={() => {
                        setStatusName("");
                      }}
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "60px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "gray",
                        fontSize: "12px",
                        zIndex: 1000,
                      }}
                    >
                      ✕
                    </span>
                  )}
                </Col>
              </Row>
            </CardHeader>
            <CustomTable
              rows={tableData.rows}
              columns={tableData.columns}
              paginationState={tableData.paginationState}
              loading={tableData.isLoading || tableData.isFetching}
              checkboxShowFalse={tableData.checkboxShowFalse}
              showPaginationOptions={false}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default BankingInvoice;
